"use client"

import { useState, useEffect } from "react"
import { getProductsByCategory } from "@/lib/api/dutchie"
import CategoryLayout from "@/components/shop/CategoryLayout"
import type { ProductDisplay } from "@/lib/api/dutchie"

/* ------------------------------------------------------------------ */
/*  Category Configuration                                            */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  "flower"
]

const EFFECTS = [
  "Relaxed", "Euphoric", "Happy", "Sleepy", "Creative",
  "Focused", "Energetic", "Uplifted", "Calm", "Pain Relief"
]

const SUBCATEGORY_REGEX: [RegExp,string][] = [
  [/indoor|in door/i, "Indoor"],
  [/outdoor|out door/i, "Outdoor"],
  [/greenhouse|green house/i, "Greenhouse"],
  [/top shelf|topshelf/i, "Top Shelf"],
  [/premium|craft/i, "Premium"],
  [/smalls|popcorn/i, "Smalls"],
  [/hydroponic|hydro/i, "Hydroponic"],
  [/organic/i, "Organic"]
]


const titleCase = (s: string) =>
  s.toLowerCase().split(" ").map(w => w[0].toUpperCase()+w.slice(1)).join(" ")

const categorizeFlower = (p: ProductDisplay) => {
  const text = `${p.name ?? ""} ${p.description ?? ""} ${p.subcategory ?? ""}`.toLowerCase()
  
  // Priority-based keyword detection
  if (text.includes('indoor') || text.includes('indoor')) {
    return 'Indoor';
  }
  if (text.includes('outdoor') || text.includes('outdoor')) {
    return 'Outdoor';
  }
  if (text.includes('greenhouse') || text.includes('green house')) {
    return 'Greenhouse';
  }
  if (text.includes('top shelf') || text.includes('topshelf')) {
    return 'Top Shelf';
  }
  if (text.includes('premium') || text.includes('craft')) {
    return 'Premium';
  }
  if (text.includes('smalls') || text.includes('popcorn')) {
    return 'Smalls';
  }
  if (text.includes('hydroponic') || text.includes('hydro')) {
    return 'Hydroponic';
  }
  if (text.includes('organic')) {
    return 'Organic';
  }
  
  // Fallback to existing subcategory with normalization
  if (p.subcategory && p.subcategory !== 'DEFAULT') {
    const normalized = p.subcategory.toLowerCase().trim();
    if (normalized.includes('indoor')) return 'Indoor';
    if (normalized.includes('outdoor')) return 'Outdoor';
    if (normalized.includes('greenhouse')) return 'Greenhouse';
    if (normalized.includes('premium') || normalized.includes('craft')) return 'Premium';
    
    return titleCase(p.subcategory.replace(/_/g, ' '));
  }
  
  return 'Other';
}

const normalizeStrainType = (strainType: string | undefined): string => {
  if (!strainType) return 'Unknown';
  const normalized = strainType.toLowerCase().trim().replace(/[_\s]+/g, ' ');
  
  if (normalized.includes('sativa') && normalized.includes('hybrid')) return 'Sativa Hybrid';
  if (normalized.includes('indica') && normalized.includes('hybrid')) return 'Indica Hybrid';
  if (normalized.includes('indica')) return 'Indica';
  if (normalized.includes('sativa')) return 'Sativa';
  if (normalized.includes('hybrid')) return 'Hybrid';
  
  return titleCase(strainType.replace(/_/g, ' '));
}

const normalizeFlowerWeight = (weight: string): string => {
  if (!weight) return ''
  const cleaned = weight.toLowerCase().trim()
  
  // Handle common flower terms
  if (/eighth/.test(cleaned)) return '3.5g'
  if (/quarter/.test(cleaned)) return '7g' 
  if (/half/.test(cleaned)) return '14g'
  if (/oz|ounce/.test(cleaned)) return '28g'
  
  // Extract numeric + g format
  const match = cleaned.match(/(\d+(?:\.\d+)?)\s*g?/)
  if (match) {
    const num = parseFloat(match[1])

    if (num === 3.5) return '3.5g'
    if (num === 4) return '4g'
    if (num === 5) return '5g'
    if (num === 7) return '7g'
    if (num === 14) return '14g'
    if (num === 28) return '28g'
    return `${num}g`
  }
  
  return ''
}

const extractFlowerWeights = (p: ProductDisplay, targetWeights: string[]): string[] => {
  const weights = new Set<string>()
  
  // Check main option field
  if (p.option) {
    const normalized = normalizeFlowerWeight(p.option)
    if (normalized && targetWeights.includes(normalized)) {
      weights.add(normalized)
    }
  }
  
  // Check variants
  p.variants?.forEach(variant => {
    if (variant.option) {
      const normalized = normalizeFlowerWeight(variant.option)
      if (normalized && targetWeights.includes(normalized)) {
        weights.add(normalized)
      }
    }
  })
  
  return Array.from(weights)
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function FlowerPage() {
  // Custom fetch function for flower products
  const fetchProducts = async (): Promise<ProductDisplay[]> => {
    try {
      console.log('Fetching products from categories:', CATEGORIES)
      
      // Get all products and filter manually to avoid stock filtering
      const { getProducts } = await import('@/lib/api/dutchie')
      const allProducts = await getProducts()
      
      console.log(`Total products available: ${allProducts.length}`)
      
      // Filter for FLOWER category
      const flowerProducts = allProducts.filter(product => {
        const category = product.category.toUpperCase()
        return category === 'FLOWER'
      })
      
      console.log(`Flower products found: ${flowerProducts.length}`)
      
      // Deduplicate by ID
      const uniqueProductsMap = new Map<string, ProductDisplay>()
      flowerProducts.forEach(product => {
        if (!uniqueProductsMap.has(product.id)) {
          uniqueProductsMap.set(product.id, product)
        }
      })
      
      const uniqueProducts = Array.from(uniqueProductsMap.values())
      console.log(`Unique products after deduplication: ${uniqueProducts.length}`)
      
      // Extract potency value from product (same logic as pre-rolls page)
      const extractPotencyValue = (product: ProductDisplay, type: 'thc' | 'cbd'): number => {
        // First try direct fields
        const directValue = type === 'thc' ? product.thc : product.cbd;
        if (directValue) {
          const match = directValue.match(/(\d+(?:\.\d+)?)/);
          if (match && match[1]) {
            return parseFloat(match[1]);
          }
        }

        // Then try potency objects
        const potencyObj = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd;
        if (potencyObj && potencyObj.formatted) {
          const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/);
          if (match && match[1]) {
            return parseFloat(match[1]);
          }
        }

        // Finally try to extract from text
        const productText = `${product.name || ''} ${product.description || ''}`.toLowerCase();
        const regex = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*%`, 'i');
        const match = productText.match(regex);

        if (match && match[1]) {
          return parseFloat(match[1]);
        }

        return 0; // Default to 0 if no value found
      }

      // Enhance products with virtual subcategory, strain type, and extracted THC/CBD values
      const enhancedProducts = uniqueProducts.map((product, index) => {
        const thcValue = extractPotencyValue(product, 'thc')
        const cbdValue = extractPotencyValue(product, 'cbd')
        
        // Debug log for first few products
        if (index < 3) {
          console.log('Product THC/CBD extraction:', {
            name: product.name,
            potencyThc: (product as any).potencyThc,
            potencyCbd: (product as any).potencyCbd,
            extractedThc: thcValue,
            extractedCbd: cbdValue
          })
        }
        
        return {
          ...product,
          virtualSubcategory: categorizeFlower(product),
          normalizedStrainType: normalizeStrainType(product.strainType),
          // Extract THC/CBD numeric values for filtering
          thc: thcValue.toString() + '%',
          cbd: cbdValue.toString() + '%'
        }
      })
      
      // Debug subcategory distribution
      const subcounts: Record<string, number> = {}
      enhancedProducts.forEach(product => {
        const sub = (product as any).virtualSubcategory
        subcounts[sub] = (subcounts[sub] || 0) + 1
      })
      console.log('Enhanced products subcategory distribution:', subcounts)
      
      console.log('fetchProducts returning enhanced products:', enhancedProducts.length)
      
      return enhancedProducts
      
    } catch (error) {
      console.error('Error fetching flower products:', error)
      return []
    }
  }
  
  const config = {
    title: "Flower",
    blurb: "Discover our curated selection of premium cannabis flower from indoor, outdoor, and greenhouse cultivations",
    fetch: fetchProducts,
    virtualSubcategory: categorizeFlower,
    effects: EFFECTS,
    potencyUnit: "%",
    showPotency: true,
    showWeight: true,
    weightRanges: [
      {
        label: "Small Amounts",
        extract: (p: ProductDisplay) => extractFlowerWeights(p, ['3.5g', '4g', '5g'])
      },
      {
        label: "Standard Amounts",
        extract: (p: ProductDisplay) => extractFlowerWeights(p, ['7g', '14g', '28g'])
      }
    ],
    filterInStock: false,
    headerTheme: "green" as const,
    extraFilterChips: (product: ProductDisplay) => {
      const strainType = normalizeStrainType(product.strainType);
      return {
        strainTypes: strainType !== 'Unknown' ? [strainType] : []
      };
    },
    debug: true
  }

  return (
    <CategoryLayout
      config={config}
      productCardProps={{ 
        buttonClassName: "bg-green-600 hover:bg-green-700"
      }}
    />
  )
}